import { UIText1, Image, UIHeading5 } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { useInView } from '@cian/valuation-utils-component';
import classNames from 'classnames';
import * as React from 'react';

import * as styles from './ChatMessageLayout.css';
import { useUserColor } from './hooks';
import { ChatPrettyDate } from '../ChatPrettyDate';
import { TypingMessage } from '../TypingMessage';

export type TChatMessageLayoutProps = {
  actionButtonsNode: React.ReactNode | null;
  bubblesNode: React.ReactNode | null;
  documentNode: React.ReactNode | null;
  errorMenuNode: React.ReactNode | null;
  errorSignNode: React.ReactNode | null;
  errorTextNode: React.ReactNode | null;
  imageAsDocumentNode: React.ReactNode | null;
  imagesNode: React.ReactNode | null;
  infoMessageNode: React.ReactNode | null;
  offerNode: React.ReactNode | null;
  statusNode: React.ReactNode | null;
  textNode: React.ReactNode | null;
  created: number;
  isOutcoming: boolean;
  isUnread: boolean;
  isTyping?: boolean;
  isOnlyImage: boolean;
  isOffer: boolean;
  isLastInChain: boolean;
  isTextAndImage: boolean;
  isChatbotOfferMessage?: boolean;
  isOfferGallery?: boolean;
  userName?: string;
  messageUserId: number;
  nameReplacementImageUrl?: string;
  showDailyrentButtons?: boolean;
  onClick?(e: React.MouseEvent): void;
  onShow?(): void;
};

export const ChatMessageLayout = React.forwardRef<HTMLDivElement, TChatMessageLayoutProps>(
  (
    {
      actionButtonsNode,
      bubblesNode,
      documentNode,
      errorMenuNode,
      errorSignNode,
      errorTextNode,
      imageAsDocumentNode,
      imagesNode,
      infoMessageNode,
      offerNode,
      statusNode,
      textNode,
      created,
      isLastInChain,
      isOffer,
      isOnlyImage,
      isOutcoming,
      isTextAndImage,
      isUnread,
      isTyping,
      isChatbotOfferMessage,
      isOfferGallery,
      messageUserId,
      nameReplacementImageUrl,
      showDailyrentButtons = false,
      userName,
      onClick,
      onShow,
    },
    parentRef,
  ) => {
    const bubbleRef = React.useRef<HTMLDivElement | null>(null);
    const userColor = useUserColor(messageUserId);

    const { ref, inView } = useInView({
      threshold: 0.5,
      triggerOnce: true,
    });

    React.useImperativeHandle(parentRef, () => bubbleRef.current as HTMLDivElement);

    React.useEffect(() => {
      if (onShow && inView) {
        onShow();
      }
    }, [inView, onShow]);

    const actionButtonsClassName = showDailyrentButtons ? styles['dailyrent-action_buttons'] : styles['action_buttons'];

    return (
      <div
        ref={ref}
        className={classNames(
          styles['container'],
          isOutcoming && styles['container_outcoming'],
          isOfferGallery && styles['container_offer_gallery'],
        )}
        data-name="ChatMessageLayout"
      >
        <div
          ref={bubbleRef}
          className={classNames(
            styles['bubble'],
            imagesNode && styles['bubble_with_image'],
            isOfferGallery && styles['bubble_offer_gallery'],
          )}
        >
          <div className={styles['row']}>
            <div
              className={classNames(
                styles['message'],
                isOutcoming ? styles['outcoming'] : styles['incoming'],
                isUnread && styles['unread'],
                isOnlyImage && styles['image_message'],
                isLastInChain && !isOfferGallery && styles['message_tail'],
                isTextAndImage && styles['image_with_text'],
                imageAsDocumentNode && styles['image_as_document'],
                (isOffer || imageAsDocumentNode) && styles['offer'],
                documentNode && styles['document'],
                styles[`message_theme_${userColor}`],
              )}
            >
              <div className={styles['content']}>
                {isTyping && <TypingMessage />}
                {userName && !nameReplacementImageUrl && (
                  <div className={classNames(styles['user_name'], styles[`user_name_${userColor}`])}>
                    <UIHeading5>{userName}</UIHeading5>
                  </div>
                )}
                {nameReplacementImageUrl && (
                  <div className={styles['name_replacement_image']}>
                    <Image height={18} src={nameReplacementImageUrl} display="inline-block" />
                  </div>
                )}
                {imagesNode}
                {textNode && (
                  <span className={styles['text_message']} data-name="ChatMessageLayout_text" onClick={onClick}>
                    <UIText1 color={isOutcoming ? 'text-inverted-default' : 'text-primary-default'}>{textNode}</UIText1>
                  </span>
                )}
              </div>
              {documentNode}
              {offerNode}
              {imageAsDocumentNode}
              {actionButtonsNode && (
                <div className={styles['content_action_buttons']}>
                  <div className={actionButtonsClassName} data-name="ChatMessageLayout_action_buttons">
                    {actionButtonsNode}
                  </div>
                </div>
              )}
              {!isTyping && !isChatbotOfferMessage && (
                <div className={styles['info']} data-name="ChatMessageLayout_info">
                  <ChatPrettyDate
                    date={created}
                    showOnlyTime
                    containerStyles={mergeStyles(styles['info_item'], styles['info_date'])}
                  />
                  <span className={styles['info_item']} data-name="ChatMessageLayout_status">
                    {statusNode}
                  </span>
                </div>
              )}
            </div>
            <div className={styles['error_sign']}>{errorSignNode}</div>
          </div>
          <div className={styles['error_text']}>{errorTextNode}</div>
          {errorMenuNode}
        </div>
        {infoMessageNode}
        {bubblesNode}
      </div>
    );
  },
);

ChatMessageLayout.displayName = 'ChatMessageLayout';
